import type { SxProps, Theme } from '@mui/material/styles';

const applicationButtonBlue = '#0090fa';
const applicationButtonBlueHover = '#007dcc';

export const signupTypography = {
  pageTitle: '30px',
  sectionHeading: '20px',
  standard: '18px',
  supporting: '14px',
} as const;

export const signupDisabledButtonStyles = {
  borderColor: 'rgba(0, 144, 250, 0.4)',
  bgcolor: 'rgba(0, 144, 250, 0.4)',
  color: 'rgba(255, 255, 255, 0.9)',
};

export const signupPageStyles = {
  page: (theme: Theme): SxProps<Theme> => ({
    width: '100%',
    height: '100vh',
    minWidth: 0,
    minHeight: 0,
    p: 0,
    overflowX: 'hidden',
    overflowY: 'auto',
    WebkitOverflowScrolling: 'touch',
    bgcolor: '#f7f7f7',
    color: theme.palette.text.primary,
  }),
  content: (theme: Theme): SxProps<Theme> => ({
    width: '100%',
    minWidth: 0,
    maxWidth: '1064px',
    minHeight: '100%',
    mx: 'auto',
    px: { xs: 2.5, sm: 5, lg: 7 },
    py: { xs: 2, sm: 2.5 },
    bgcolor: theme.palette.background.paper,
    boxSizing: 'border-box',
  }),
  logoHeader: (): SxProps<Theme> => ({
    display: 'flex',
    justifyContent: 'center',
  }),
  logo: (): SxProps<Theme> => ({
    display: 'block',
    width: { xs: '140px', sm: '170px' },
    height: 'auto',
  }),
  accentLink: (theme: Theme): SxProps<Theme> => ({
    color: theme.palette.success.dark,
    fontWeight: 500,
    '&:focus-visible': {
      outline: `3px solid ${theme.palette.success.light}`,
      outlineOffset: '3px',
      borderRadius: 1,
    },
  }),
  title: (): SxProps<Theme> => ({
    fontSize: signupTypography.pageTitle,
    fontWeight: 400,
    lineHeight: 1.15,
  }),
  form: (): SxProps<Theme> => ({
    width: '100%',
    minWidth: 0,
  }),
  columns: (): SxProps<Theme> => ({
    display: 'grid',
    gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
    gap: { xs: 6, md: 0 },
    alignItems: 'stretch',
  }),
  productForm: (): SxProps<Theme> => ({
    width: '100%',
    minWidth: 0,
    maxWidth: '620px',
    mx: 'auto',
    display: 'grid',
    gap: { xs: 3.5, sm: 4 },
  }),
  tokenRecovery: (): SxProps<Theme> => ({
    width: '100%',
    minWidth: 0,
    display: 'grid',
    gap: 2,
  }),
  tokenRecoveryHeading: (): SxProps<Theme> => ({
    m: 0,
    fontSize: signupTypography.sectionHeading,
    fontWeight: 400,
  }),
  tokenRecoveryError: (theme: Theme): SxProps<Theme> => ({
    m: 0,
    color: theme.palette.error.main,
    fontSize: signupTypography.standard,
    lineHeight: 1.45,
  }),
  column: (theme: Theme, side: 'left' | 'right'): SxProps<Theme> => ({
    minWidth: 0,
    height: '100%',
    ...(side === 'left'
      ? {
          pr: { md: 7 },
        }
      : {
          pl: { md: 7 },
          borderLeft: { md: `1px solid ${theme.palette.divider}` },
        }),
  }),
  productSection: (): SxProps<Theme> => ({
    minWidth: 0,
    width: '100%',
  }),
  accountHeading: (): SxProps<Theme> => ({
    mb: 2.5,
    fontSize: signupTypography.sectionHeading,
    fontWeight: 400,
  }),
  fieldGroup: (): SxProps<Theme> => ({
    mb: 2.75,
  }),
  input: (): SxProps<Theme> => ({
    width: '100%',
    minWidth: 0,
    '& .MuiOutlinedInput-root, &.MuiOutlinedInput-root': {
      height: '49px',
      minHeight: '49px',
      fontSize: signupTypography.standard,
    },
    '& .MuiInputLabel-root': {
      fontSize: signupTypography.standard,
    },
  }),
  inlineAction: (theme: Theme): SxProps<Theme> => ({
    p: 0,
    minWidth: 0,
    justifySelf: 'start',
    color: theme.palette.success.dark,
    fontSize: signupTypography.standard,
    fontWeight: 500,
    '&:hover': {
      bgcolor: 'transparent',
      textDecoration: 'underline',
    },
    '&:focus-visible': {
      outline: `3px solid ${theme.palette.success.light}`,
      outlineOffset: '4px',
    },
  }),
  emailButton: (theme: Theme): SxProps<Theme> => ({
    height: '49px',
    minHeight: '49px',
    border: '1px solid #6bc0ff',
    borderRadius: 1,
    bgcolor: applicationButtonBlue,
    color: theme.palette.common.white,
    fontSize: signupTypography.standard,
    fontWeight: 400,
    textTransform: 'none',
    '&:hover': {
      borderColor: applicationButtonBlueHover,
      bgcolor: applicationButtonBlueHover,
    },
    '&:focus-visible': {
      outline: '3px solid rgba(0, 144, 250, 0.35)',
      outlineOffset: '3px',
    },
    '&.Mui-disabled': {
      ...signupDisabledButtonStyles,
    },
  }),
  actionError: (theme: Theme): SxProps<Theme> => ({
    mt: -2,
    mb: 2,
    color: theme.palette.error.main,
    fontSize: signupTypography.supporting,
    lineHeight: 1.45,
  }),
  consentError: (theme: Theme): SxProps<Theme> => ({
    mt: 1,
    color: theme.palette.error.main,
    fontSize: signupTypography.supporting,
  }),
  stagedForm: (): SxProps<Theme> => ({
    width: '100%',
    minWidth: 0,
    maxWidth: '720px',
    mx: 'auto',
  }),
  stepContent: (): SxProps<Theme> => ({
    width: '100%',
    minWidth: 0,
  }),
  sectionDescription: (theme: Theme): SxProps<Theme> => ({
    mt: -2,
    mb: 2,
    color: theme.palette.grey[700],
    fontSize: signupTypography.standard,
  }),
  continueButton: (theme: Theme): SxProps<Theme> => ({
    height: '49px',
    minWidth: { xs: '100%', sm: '210px' },
    px: 4,
    border: '1px solid #6bc0ff',
    borderRadius: 1,
    bgcolor: applicationButtonBlue,
    color: theme.palette.common.white,
    fontSize: signupTypography.standard,
    fontWeight: 600,
    textTransform: 'none',
    '&:hover': {
      borderColor: applicationButtonBlueHover,
      bgcolor: applicationButtonBlueHover,
    },
    '&:focus-visible': {
      outline: '3px solid rgba(0, 144, 250, 0.35)',
      outlineOffset: '3px',
    },
    '&.Mui-disabled': {
      ...signupDisabledButtonStyles,
    },
  }),
  summaryStack: (): SxProps<Theme> => ({
    display: 'grid',
    gap: 1.25,
    mb: 3,
  }),
};
